import React, { useEffect, useMemo } from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useSaveActionsContext } from "context/save";

import useSpecialOfferApi from "hooks/api/private/useSpecialOfferApi";
import { useSpecialOfferId } from "hooks/useId";

import { LanguageEnum } from "types/api/commons";
import { SpecialOfferBody, SpecialOfferDetails } from "types/api/specialOffer";

import { getExtraErrorsDescription } from "utils/validators";

export type SpecialOfferFormType = {
    id?: string;
    enabled: boolean;
    text: {
        [x in LanguageEnum]: {
            title: string;
            subtitle: string;
            description: string;
        };
    };
};

export type SpecialOfferFormHook = {
    formHook: FormHookType<SpecialOfferFormType>;
};

function useSpecialOfferForm(): SpecialOfferFormHook {
    const specialOfferId = useSpecialOfferId();

    const { createSpecialOffer, updateSpecialOffer, getOneSpecialOffer } = useSpecialOfferApi();

    const schema = useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                enabled: {
                    type: "simple",
                    validators: [],
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            title: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            subtitle: {
                                type: "simple",
                                validators: [],
                            },
                            description: {
                                type: "simple",
                                validators: [],
                            },
                        },
                    },
                },
            },
        }),
        [],
    );

    const initialValues: SpecialOfferFormType = React.useMemo(
        () => ({
            enabled: true,
            text: {
                it: {
                    title: "",
                    subtitle: "",
                    description: "",
                },
                en: {
                    title: "",
                    subtitle: "",
                    description: "",
                },
            },
        }),
        [],
    );

    const formHook = useForm<SpecialOfferFormType>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const [offerDetails, setOfferDetails] = React.useState<SpecialOfferDetails | undefined>(
        undefined,
    );

    React.useEffect(() => {
        if (specialOfferId) {
            const [apiCall, abortController] = getOneSpecialOffer({ id: specialOfferId });

            apiCall.then(setOfferDetails);

            return () => {
                abortController.abort();
            };
        }
    }, [getOneSpecialOffer, specialOfferId]);

    React.useEffect(() => {
        if (offerDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = offerDetails.id;
                newForm.enabled = offerDetails.enabled;

                newForm.text.it.title = offerDetails.text.it?.title ?? "";
                newForm.text.it.subtitle = offerDetails.text.it?.subtitle ?? "";
                newForm.text.it.description = offerDetails.text.it?.description ?? "";

                newForm.text.en.title = offerDetails.text.en?.title ?? "";
                newForm.text.en.subtitle = offerDetails.text.en?.subtitle ?? "";
                newForm.text.en.description = offerDetails.text.en?.description ?? "";

                return newForm;
            });
        }
    }, [setForm, offerDetails]);

    const { addSaveAction } = useSaveActionsContext();

    useEffect(() => {
        addSaveAction("useSpecialOfferForm", () => {
            if (isValid) {
                const body: SpecialOfferBody = {
                    enabled: form.enabled,
                    text: {
                        it: {
                            title: form.text.it.title,
                            subtitle: form.text.it.subtitle,
                            description: form.text.it.description,
                        },
                        en: {
                            title: form.text.en.title,
                            subtitle: form.text.en.subtitle,
                            description: form.text.en.description,
                        },
                    },
                };

                if (specialOfferId) {
                    const [apiCall] = updateSpecialOffer({ id: specialOfferId }, body);

                    return apiCall;
                } else {
                    const [apiCall] = createSpecialOffer(body);

                    return apiCall;
                }
            } else {
                showAllError();

                return Promise.reject(new Error("INVALID_FORM"));
            }
        });
    }, [addSaveAction, showAllError, createSpecialOffer, updateSpecialOffer, isValid, specialOfferId, form]);

    return React.useMemo(
        (): SpecialOfferFormHook => ({
            formHook,
        }),
        [formHook],
    );
}

export default useSpecialOfferForm;
